import React from 'react';
import { EntityLayout, EntitySwitch } from '@backstage/plugin-catalog';
import { RTLLinksCard } from '../../cards/LinksCard';
import { ContactChannelsCard } from '../../cards/ContactChannelsCard';
import { Grid } from '@material-ui/core';
import { Direction, RelationsGraphCard } from '../../cards/RelationsGraphCard';
import { WarningCard } from '../../cards/WarningCard';
import { GroupProfileCard } from '../../cards/GroupProfileCard';
import { EntraGroupsGrid } from './MemberListCard';
import {
  CLOUD_TYPES,
  HasFilteredResourcesCard,
  useTeamResourceTypes,
} from '../../cards/HasFilteredResourcesCard';
import { HasComponentsCard } from '../../cards/HasComponentsCard';
import {
  GroupEntity,
  Entity,
  RELATION_CHILD_OF,
  RELATION_DEPENDENCY_OF,
  RELATION_DEPENDS_ON,
  RELATION_MEMBER_OF,
  RELATION_OWNER_OF,
  RELATION_PARENT_OF,
  RELATION_PART_OF,
} from '@backstage/catalog-model';
import { HasApisCard } from '../../cards/HasApisCard';
import { HasProductsCard } from '../../cards/HasProductCard';
import { TechradarCard } from '../../cards/TechradarCard';
import { HasSystemsCard } from '../../cards/HasSystemsCard';
import {
  cmdbUidOf,
  hasApis,
  hasComponents,
  hasContactChannels,
  hasKubernetes,
  hasProducts,
  hasSystems,
  isNotAdGroup,
} from '../../cards/utils';
import { useEntity } from '@backstage/plugin-catalog-react';
import { EntraGroupsIn } from '../../../hooks';

export function TeamPage() {
  const { entity: group } = useEntity<GroupEntity>();

  // The model api needs the CMDB uid as a path segment - it addresses a
  // directory in the CMDB repository. The entity is named after the vuid, so
  // the uid comes from the annotation.
  const teamUID = cmdbUidOf(group);
  const teamCmdbKey =
    group.metadata.annotations!['schema.dp.netrtl.com/legacy-short-key'];
  const cmdbGroups = (group.spec as any).cmdbGroups as
    | EntraGroupsIn
    | undefined;
  // While the answer is out the tab stays, as on the department page: hiding
  // it would say the team holds nothing before that is known.
  const { types, loading } = useTeamResourceTypes(group.metadata.name);
  const holdsAnyOf = (candidates: string[]) =>
    loading || candidates.some(type => types.has(type));

  return (
    <EntityLayout>
      <EntityLayout.Route path="/" title="Overview">
        <Grid container spacing={3} alignItems="stretch">
          <WarningCard />
          <Grid item xs={12} md={6}>
            <GroupProfileCard />
          </Grid>
          <Grid item md={6} xs={12}>
            <RelationsGraphCard
              height={400}
              // `ownerOf` is the relation a product is hung off its team with,
              // but services carry it as well, so they are left out of the
              // kinds to keep the graph to the team, its domain and what it
              // owns. Its Entra groups are left out too, see isNotAdGroup -
              // they are what the Groups tab is for.
              kinds={['Group', 'Domain', 'Product']}
              entityFilter={isNotAdGroup}
              relations={[
                RELATION_CHILD_OF,
                RELATION_PARENT_OF,
                RELATION_PART_OF,
                RELATION_MEMBER_OF,
                RELATION_DEPENDS_ON,
                RELATION_DEPENDENCY_OF,
                RELATION_OWNER_OF,
              ]}
              direction={Direction.TOP_BOTTOM}
            />
          </Grid>
          <RTLLinksCard />
          <EntitySwitch>
            <EntitySwitch.Case if={hasContactChannels}>
              <Grid item xs={12}>
                <ContactChannelsCard />
              </Grid>
            </EntitySwitch.Case>
          </EntitySwitch>
        </Grid>
      </EntityLayout.Route>
      {/*
        The Entra groups of the team and who is in them. They are a tab of
        their own rather than a row of cards below the overview: a team runs
        one card per group, each of them a member table, and together they
        push everything the overview is there for off the screen.

        The very same tab the department page shows, see EntraGroupsGrid.
      */}
      <EntityLayout.Route path="/groups" title="Groups">
        <EntraGroupsGrid
          groups={cmdbGroups}
          target={{
            kind: 'team',
            cmdbTeamUID: teamUID,
            cmdbTeamShortID: teamCmdbKey,
          }}
        />
      </EntityLayout.Route>
      <EntityLayout.Route path="/products" title="Products" if={hasProducts}>
        <Grid container spacing={3} alignItems="stretch">
          <Grid item xs={12}>
            <HasProductsCard />
          </Grid>
        </Grid>
      </EntityLayout.Route>
      {/*
        Only for a team that holds a cloud account: any resource at all would
        put the tab up for a team that only has Kubernetes, with nothing but
        empty cards on it. See useTeamResourceTypes for why the relations of
        the team cannot tell, and the department page for the same gate.
      */}
      {holdsAnyOf(CLOUD_TYPES) && (
        <EntityLayout.Route path="/cloud" title="Cloud Accounts">
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <HasFilteredResourcesCard
                type="gcp-project"
                title="GCP Projects"
              />
            </Grid>
            <Grid item xs={12}>
              <HasFilteredResourcesCard
                type="aws-account"
                title="AWS Accounts"
              />
            </Grid>
            <Grid item xs={12}>
              <HasFilteredResourcesCard
                type="azure-subscription"
                title="Azure Subscriptions"
              />
            </Grid>
            <Grid item xs={12}>
              <HasFilteredResourcesCard
                type="oci-account"
                title="OCI Accounts"
              />
            </Grid>
          </Grid>
        </EntityLayout.Route>
      )}
      <EntityLayout.Route
        path="/kubernetes"
        title="Kubernetes"
        if={hasKubernetes}
      >
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <HasFilteredResourcesCard
              type="k8s-cluster"
              title="Kubernetes Clusters"
            />
          </Grid>
          <Grid item xs={12}>
            {/*
              The clusters and slices of every product of the team are mixed
              here, so each row names its product; a cluster and a slice of it
              carry the same name in dev and in prod, which the Environment
              column tells apart.
            */}
            <HasFilteredResourcesCard
              type="k8s-cluster-slice"
              title="Kubernetes Cluster Slices"
            />
          </Grid>
        </Grid>
      </EntityLayout.Route>
      <EntityLayout.Route
        path="/components"
        title="Components"
        if={hasComponents}
      >
        <Grid container spacing={3} alignItems="stretch">
          <Grid item xs={12}>
            <HasComponentsCard />
          </Grid>
        </Grid>
      </EntityLayout.Route>
      <EntityLayout.Route path="/systems" title="Systems" if={hasSystems}>
        <Grid container spacing={3} alignItems="stretch">
          <Grid item xs={12}>
            <HasSystemsCard />
          </Grid>
        </Grid>
      </EntityLayout.Route>
      <EntityLayout.Route path="/apis" title="APIs" if={hasApis}>
        <Grid container spacing={3} alignItems="stretch">
          <Grid item xs={12}>
            <HasApisCard />
          </Grid>
        </Grid>
      </EntityLayout.Route>
      {/*
        Last of the tabs: the radar is about what the team works with, not
        about what it is or what it runs, and the tabs before it all answer
        the latter.
      */}
      <EntityLayout.Route
        path="/tech-radar"
        title="Tech Radar"
        if={(entity: Entity) => entity?.spec?.type === 'team'}
      >
        <TechradarCard width={2000} height={1000} pageTitle="Team Radar" />
      </EntityLayout.Route>
    </EntityLayout>
  );
}
